import { defineStore } from 'pinia';
import { SelectLists, SelectOption, SiteSettings } from './root-types';
import { ProjectState } from '../project/project-types';
import { TaskState } from '../project-task/project-task-types';
import { Role } from '../permission/roles';
import { TrafficState } from '../traffic/types/traffic-enums';
import { Currency, CurrencyConfig } from '../utils/currency-config';
import { HolidayType } from '../shared/shared-types';
import { PartnerType } from '../partner/partner-types';
import { HolidayState } from '../holiday/holidays-enums';

export const useRootStore = defineStore({
  id: 'root',

  state: () => ({
    companies: [] as SelectOption[],
    appLocale: null as string | null,
    locales: [] as string[],
    selectLists: {} as SelectLists,
    siteSettings: null as SiteSettings | null,
    trafficLocked: false,
    trafficFinalized: false,
    autoSaveInProgress: false,
    objectStates: {
      project: [] as ProjectState[],
      project_task: [] as TaskState[],
      traffic: [] as TrafficState[],
      holiday_request: [] as HolidayState[],
      project_disabled_states: [] as string[],
    },
    objectClosedStates: {
      project: [] as ProjectState[],
      project_task: [] as TaskState[],
    },
    countries: [] as string[],
    godModeRole: '' as Role,
    rolesCanModifyProjects: [] as Role[],
    isApprovalUser: false,
    haveUrgentTraffic: false,
    defaultCurrency: null as Currency | null,
    currencyConfig: null as { [key in Currency]: CurrencyConfig } | null,
    holidayTypes: [] as HolidayType[],
    allHolidayTypes: [] as HolidayType[],
    partnerTypes: [] as PartnerType[],
    trafficAllowedDates: [] as string[],
  }),

  getters: {
    availableCurrencies(): Currency[] {
      if (!this.siteSettings) {
        return [];
      }

      return this.siteSettings.currencies.available_currencies.map(
        (item) => item.name as Currency
      );
    },
  },

  actions: {
    setCompanies(value: SelectOption[]): void {
      this.companies = value;
    },
    setLocales(locales: string[]): void {
      this.locales = locales;
    },
    setAppLocales(locale: string): void {
      this.appLocale = locale;
    },
    setSiteSettings(value: SiteSettings | null): void {
      this.siteSettings = value;
    },
    setObjectStates(value: any): void {
      this.objectStates = value;
    },
    setObjectClosedStates(value: any): void {
      this.objectClosedStates = value;
    },
    setTrafficLock(value: boolean): void {
      this.trafficLocked = value;
    },
    setTrafficFinalized(value: boolean): void {
      this.trafficFinalized = value;
    },
    setAutoSaveProgress(value: boolean): void {
      this.autoSaveInProgress = value;
    },
    setCountries(countries: string[]): void {
      this.countries = countries;
    },
    setRolesCanModifyProjects(roles: Role[]): void {
      this.rolesCanModifyProjects = roles;
    },
    setGodModeRole(role: Role): void {
      this.godModeRole = role;
    },
    setUrgentTraffic(haveUrgent: boolean): void {
      this.haveUrgentTraffic = haveUrgent;
    },
    setDefaultCurrency(currency: Currency): void {
      this.defaultCurrency = currency;
    },
    setCurrencyConfigs(currencyConfigs: {
      [key in Currency]: CurrencyConfig;
    }): void {
      this.currencyConfig = currencyConfigs;
    },
    setHolidayTypes(types: HolidayType[]): void {
      this.holidayTypes = types;
    },
    setAllHolidayTypes(types: HolidayType[]): void {
      this.allHolidayTypes = types;
    },
    setPartnerTypes(types: PartnerType[]): void {
      this.partnerTypes = types;
    },
    setIsApprovalUser(value: boolean): void {
      this.isApprovalUser = value;
    },
    setTrafficAllowedDates(value: string[]): void {
      this.trafficAllowedDates = value;
    },
  },
});
